import type { UserResponse, BundleRecommendation, ToolBundle, ScoringType } from '../../types/prioritization-selector';
import { getBundleById, getBundlesByIds } from '../../data/prioritization-selector/bundles';
import { getMethodsByIds, bundleMethodMap } from '../../data/prioritization-selector/methods';
import { getNextQuestion } from '../../data/prioritization-selector/decisionTree';

export class DecisionEngine {
  private responses: UserResponse[] = [];
  private path: string[] = [];

  constructor(responses: UserResponse[] = []) {
    this.responses = responses;
  }

  addResponse(questionId: string, value: string | string[] | number): void {
    // Remove existing response for this question
    this.responses = this.responses.filter(r => r.questionId !== questionId);
    
    // Add new response
    this.responses.push({ questionId, value });
    
    // Update path
    if (!this.path.includes(questionId)) {
      this.path.push(questionId);
    }
  }

  getCurrentQuestion(): string | null {
    if (this.path.length === 0) return 'Q1';
    
    const lastQuestionId = this.path[this.path.length - 1];
    const lastResponse = this.responses.find(r => r.questionId === lastQuestionId);
    
    if (!lastResponse) return null;
    
    const nextQuestion = getNextQuestion(lastQuestionId, lastResponse.value as string);
    return nextQuestion?.id || null;
  }

  getRecommendation(): BundleRecommendation | null {
    const baseBundles = this.findBaseBundles();
    const addOns = this.findAddOns();
    const method = this.findMethod();
    
    if (baseBundles.length === 0) return null;

    const primaryBundle = getBundleById(baseBundles[0]);
    if (!primaryBundle) return null;

    const addOnBundles = getBundlesByIds(addOns);
    
    // Get recommended methods for the primary bundle, filtered by scoring preference
    const recommendedMethodIds = bundleMethodMap[primaryBundle.id] || [];
    const allBundleMethods = getMethodsByIds(recommendedMethodIds);
    const recommendedMethods = this.filterMethodsByScoringPreference(allBundleMethods);
    
    return {
      primaryBundle,
      addOns: addOnBundles,
      recommendedMethods,
      reasoning: this.generateReasoning(),
      confidence: this.calculateConfidence(),
      confidenceExplanation: this.generateConfidenceExplanation(),
      implementation: this.generateImplementation(primaryBundle, addOnBundles, method),
      nextSteps: this.generateNextSteps(primaryBundle, addOnBundles, method)
    };
  }

  private findBaseBundles(): string[] {
    const q1 = this.getResponseValue('Q1');
    const q2 = this.getResponseValue('Q2A') || this.getResponseValue('Q2B') || this.getResponseValue('Q2C');
    const q3 = this.getResponseValue('Q3A') || this.getResponseValue('Q3B') || this.getResponseValue('Q3C');

    // Base bundle rules from v3 Working Draft
    if (q1 === 'task') {
      if (q2 === 'solo') {
        const time = this.getResponseValue('Qa');
        // B2 (Solo-Week) for planned multi-hour work; B1 (Solo-Now) for quick/daily triage
        return time === 'time_hours' || time === 'time_days' ? ['B2'] : ['B1'];
      } else if (q2 === 'team') {
        if (q3 === 'items_3_7') {
          return ['B4']; // Team-Decision
        } else if (q3 === 'items_many') {
          return ['B3']; // Team-Backlog
        }
      }
    } else if (q1 === 'initiative') {
      if (q2 === 'high_uncertainty') {
        if (q3 === 'deps_high') {
          return ['B6', 'B7']; // Discovery + Dependency-Heavy
        } else if (q3 === 'deps_low') {
          return ['B6']; // Discovery
        }
      } else if (q2 === 'low_med_uncertainty') {
        if (q3 === 'one_way_yes') {
          return ['B3', 'B8']; // Team-Backlog + High-Stakes
        } else if (q3 === 'one_way_no') {
          return ['B3']; // Team-Backlog
        }
      }
    } else if (q1 === 'portfolio') {
      if (q2 === 'cross_team_deps_yes') {
        return ['B5', 'B7']; // Portfolio-Quarter + Dependency-Heavy
      } else if (q2 === 'cross_team_deps_no') {
        return ['B5']; // Portfolio-Quarter
      }
    }

    return [];
  }

  private findAddOns(): string[] {
    const addOns: string[] = [];
    
    // Q4: Beneficiary = others → add B9 (Delegation)
    if (this.getResponseValue('Q4') === 'others') {
      addOns.push('B9');
    }
    
    // Q5: Urgent & Hard to Reverse = Yes → add B8 (Safeguards), unless already present
    if (this.getResponseValue('Q5') === 'urgent_hard_yes') {
      const baseBundles = this.findBaseBundles();
      if (!baseBundles.includes('B8')) {
        addOns.push('B8');
      }
    }
    
    return addOns;
  }

  /**
   * Re-order bundle methods to front-load those matching the user's scoring preference (Qb).
   * Falls back to the full list when nothing matches, rather than returning an empty set.
   * The intent: honour Wodtke's principle that a simpler method executed well beats
   * a complex one executed poorly — don't hand someone RICE if they said "no formulas".
   */
  private filterMethodsByScoringPreference(
    methods: ReturnType<typeof getMethodsByIds>
  ): ReturnType<typeof getMethodsByIds> {
    const scoring = this.getResponseValue('Qb');
    if (!scoring || methods.length === 0) return methods;

    const preferredTypes: ScoringType[] = scoring === 'scoring_quantitative'
      ? ['quantitative', 'light-scoring', 'qualitative']
      : scoring === 'scoring_light'
        ? ['light-scoring', 'qualitative', 'quantitative']
        : ['qualitative', 'light-scoring', 'quantitative']; // scoring_visual

    const sorted = [...methods].sort((a, b) => {
      const ai = preferredTypes.indexOf(a.scoringType);
      const bi = preferredTypes.indexOf(b.scoringType);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    return sorted;
  }

  private findMethod(): string {
    const volume = this.getResponseValue('Q6');
    const scoring = this.getResponseValue('Qb');
    const isQuantitative = scoring === 'scoring_quantitative';
    const isLight = scoring === 'scoring_light';

    if (volume === '≤10') {
      return isQuantitative ? 'RICE_or_pairwise' : 'pairwise_or_stack_rank';
    } else if (volume === '11-50') {
      if (isQuantitative) return 'RICE_scored';
      if (isLight) return '2x2_or_RICE_then_sort';
      return '2x2_or_dot_vote';
    } else if (volume === '>50') {
      return isQuantitative ? 'WSJF_or_RICE_batch' : 'MoSCoW_then_RICE';
    }

    return 'pairwise_or_stack_rank'; // default
  }

  private getResponseValue(questionId: string): string | null {
    const response = this.responses.find(r => r.questionId === questionId);
    return response ? response.value as string : null;
  }

  private generateReasoning(): string {
    const reasons: string[] = [];
    
    const q1 = this.getResponseValue('Q1');
    const q2 = this.getResponseValue('Q2A') || this.getResponseValue('Q2B') || this.getResponseValue('Q2C');
    const q4 = this.getResponseValue('Q4');
    const q5 = this.getResponseValue('Q5');
    const q6 = this.getResponseValue('Q6');
    const qa = this.getResponseValue('Qa');
    const qb = this.getResponseValue('Qb');
    
    if (q1 === 'task') reasons.push('Task-level prioritization');
    else if (q1 === 'initiative') reasons.push('Initiative-level planning');
    else if (q1 === 'portfolio') reasons.push('Portfolio-level management');
    
    if (q2 === 'solo') reasons.push('Individual decision-making');
    else if (q2 === 'team') reasons.push('Team collaboration required');
    else if (q2 === 'high_uncertainty') reasons.push('High uncertainty — discovery approach');
    else if (q2 === 'low_med_uncertainty') reasons.push('Low to medium uncertainty');
    else if (q2 === 'cross_team_deps_yes') reasons.push('Cross-team dependencies require coordination');
    else if (q2 === 'cross_team_deps_no') reasons.push('Limited cross-team coordination needed');
    
    if (q4 === 'others') reasons.push('Delegation to others');
    else if (q4 === 'self') reasons.push('Personal prioritization');
    
    if (q5 === 'urgent_hard_yes') reasons.push('High-stakes — safeguards needed');
    
    if (q6 === '≤10') reasons.push('Small item set (≤10)');
    else if (q6 === '11-50') reasons.push('Medium item set (11–50)');
    else if (q6 === '>50') reasons.push('Large item set (>50)');
    
    if (qa === 'time_quick') reasons.push('Quick session (<30 min)');
    else if (qa === 'time_hours') reasons.push('Workshop-length session (hours)');
    else if (qa === 'time_days') reasons.push('Full planning cycle (days)');
    
    if (qb === 'scoring_visual') reasons.push('Visual/intuitive scoring preferred');
    else if (qb === 'scoring_light') reasons.push('Light criteria-based scoring');
    else if (qb === 'scoring_quantitative') reasons.push('Quantitative formula scoring');
    
    return reasons.join(', ');
  }
  
  private generateConfidenceExplanation(): string {
    const confidence = this.calculateConfidence();
    const q1 = this.getResponseValue('Q1');
    const q2 = this.getResponseValue('Q2A') || this.getResponseValue('Q2B') || this.getResponseValue('Q2C');
    const q6 = this.getResponseValue('Q6');
    const qa = this.getResponseValue('Qa');
    const qb = this.getResponseValue('Qb');
    
    if (confidence === 'high') {
      return 'High confidence — scope, mode, volume, time budget, and scoring preference are all captured. The recommendation is tightly matched to your context.';
    } else if (confidence === 'medium') {
      const missing: string[] = [];
      if (!qa) missing.push('time budget');
      if (!qb) missing.push('scoring preference');
      const missingNote = missing.length > 0 ? ` Adding ${missing.join(' and ')} would sharpen the recommendation.` : '';
      return `Medium confidence — the core decision path is clear.${missingNote}`;
    } else {
      const missing: string[] = [];
      if (!q1) missing.push('scope');
      if (!q2) missing.push('decision mode');
      if (!q6) missing.push('item volume');
      if (!qa) missing.push('time available');
      if (!qb) missing.push('scoring preference');
      return `Lower confidence — ${missing.join(', ')} ${missing.length === 1 ? 'is' : 'are'} unclear. Consider completing the full questionnaire for a better-matched recommendation.`;
    }
  }

  private calculateConfidence(): 'high' | 'medium' | 'low' {
    let confidenceScore = 0;
    
    const q1 = this.getResponseValue('Q1');
    const q2 = this.getResponseValue('Q2A') || this.getResponseValue('Q2B') || this.getResponseValue('Q2C');
    const q3 = this.getResponseValue('Q3A') || this.getResponseValue('Q3B') || this.getResponseValue('Q3C');
    const q4 = this.getResponseValue('Q4');
    const q5 = this.getResponseValue('Q5');
    const q6 = this.getResponseValue('Q6');
    const qa = this.getResponseValue('Qa');
    const qb = this.getResponseValue('Qb');
    
    // Core decision path (0-40 points)
    if (q1) confidenceScore += 10;
    if (q2) confidenceScore += 10;
    if (q3) confidenceScore += 10;
    if (q4) confidenceScore += 5;
    if (q5) confidenceScore += 5;
    
    // Volume (0-10 points) — still important but shared weight with new dimensions
    if (q6) confidenceScore += 10;
    
    // New dimensions — time and scoring preference sharpen the recommendation
    if (qa) confidenceScore += 5;
    if (qb) confidenceScore += 5;
    
    // Decision path quality (0-20 points)
    if (this.hasCompleteDecisionPath()) confidenceScore += 20;
    
    // Response consistency (0-20 points)
    confidenceScore += this.calculateResponseConsistency();
    
    if (confidenceScore >= 80) return 'high';
    if (confidenceScore >= 60) return 'medium';
    return 'low';
  }
  
  private hasCompleteDecisionPath(): boolean {
    const q1 = this.getResponseValue('Q1');
    const q2 = this.getResponseValue('Q2A') || this.getResponseValue('Q2B') || this.getResponseValue('Q2C');
    const q3 = this.getResponseValue('Q3A') || this.getResponseValue('Q3B') || this.getResponseValue('Q3C');
    const qa = this.getResponseValue('Qa');
    const qb = this.getResponseValue('Qb');

    // Qa and Qb are now required for a complete path
    if (!qa || !qb) return false;
    
    if (q1 === 'task') {
      return q2 === 'solo' || (q2 === 'team' && !!q3);
    } else if (q1 === 'initiative') {
      return (q2 === 'high_uncertainty' && !!q3) || (q2 === 'low_med_uncertainty' && !!q3);
    } else if (q1 === 'portfolio') {
      return q2 === 'cross_team_deps_yes' || q2 === 'cross_team_deps_no';
    }
    
    return false;
  }
  
  private calculateResponseConsistency(): number {
    let consistencyScore = 0;
    
    const q1 = this.getResponseValue('Q1');
    const q2 = this.getResponseValue('Q2A') || this.getResponseValue('Q2B') || this.getResponseValue('Q2C');
    const q4 = this.getResponseValue('Q4');
    const q5 = this.getResponseValue('Q5');
    const q6 = this.getResponseValue('Q6');
    
    // Check for logical consistency
    if (q1 === 'task' && q2 === 'solo' && q4 === 'self') {
      consistencyScore += 5; // Solo task for self is consistent
    }
    
    if (q1 === 'portfolio' && q2 && q2.includes('cross_team')) {
      consistencyScore += 5; // Portfolio with cross-team makes sense
    }
    
    if (q5 === 'urgent_hard_yes' && q1 === 'initiative') {
      consistencyScore += 5; // High-stakes initiatives are common
    }
    
    if (q6 === '≤10' && q2 === 'solo') {
      consistencyScore += 5; // Small lists for solo work is consistent
    }
    
    return Math.min(consistencyScore, 20); // Cap at 20 points
  }

  private generateImplementation(primaryBundle: ToolBundle, _addOns: ToolBundle[], method: string): string[] {
    const steps: string[] = [];
    
    // Get the recommended methods for better step descriptions
    const recommendedMethodIds = bundleMethodMap[primaryBundle.id] || [];
    const recommendedMethods = getMethodsByIds(recommendedMethodIds);
    
    if (recommendedMethods.length > 0) {
      steps.push(`Start with ${recommendedMethods[0].name}: ${recommendedMethods[0].description}`);
      
      if (recommendedMethods.length > 1) {
        steps.push(`Also consider: ${recommendedMethods.slice(1).map(m => m.name).join(', ')}`);
      }
    }
    
    // Add method-specific guidance
    const methodName = method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    steps.push(`Use ${methodName} approach for ranking and scoring items`);
    
    return steps;
  }

  private generateNextSteps(primaryBundle: ToolBundle, _addOns: ToolBundle[], method: string): string[] {
    const steps: string[] = [];
    
    // Get the recommended methods for better step descriptions
    const recommendedMethodIds = bundleMethodMap[primaryBundle.id] || [];
    const recommendedMethods = getMethodsByIds(recommendedMethodIds);
    
    if (recommendedMethods.length > 0) {
      steps.push(`Start with ${recommendedMethods[0].name} to prioritize your items`);
      
      if (recommendedMethods.length > 1) {
        steps.push(`Consider using ${recommendedMethods.slice(1).map(m => m.name).join(', ')} as complementary approaches`);
      }
    }
    
    // Add method-specific guidance
    const methodName = method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    steps.push(`Apply the ${methodName} approach for ranking and scoring`);
    steps.push('Review your prioritization results and adjust as needed');
    steps.push('Set up regular check-ins to track progress and refine priorities');
    
    return steps;
  }

  isComplete(): boolean {
    const currentQuestion = this.getCurrentQuestion();
    return currentQuestion === null;
  }

  getPath(): string[] {
    return [...this.path];
  }

  getResponses(): UserResponse[] {
    return [...this.responses];
  }
}

export const createDecisionEngine = (responses: UserResponse[] = []): DecisionEngine => {
  return new DecisionEngine(responses);
};

